import { CheckSquare, FileText, Scale, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { useChatContext } from "@/stores/chat-context-store";
import { useChecklistStatusConfig } from "@/components/audits/tabs/checklist/ChecklistRow";
import type { ChecklistItem, ChecklistItemDetailResult } from "./chatCardUtils";
import type { ChecklistItemStatus } from "@/packages/types";

interface ChecklistItemCardProps {
  item: ChecklistItem | ChecklistItemDetailResult;
  variant?: "compact" | "detail";
}

function isDetailResult(
  item: ChecklistItem | ChecklistItemDetailResult,
): item is ChecklistItemDetailResult {
  return "categoryName" in item;
}

export function ChecklistItemCard({
  item,
  variant = "compact",
}: ChecklistItemCardProps) {
  const { t } = useTranslation("chat");
  const { addEntity } = useChatContext();
  const statusConfigs = useChecklistStatusConfig();
  const statusConfig = statusConfigs[item.status as ChecklistItemStatus];
  const detail = isDetailResult(item) ? item : null;

  const handleClick = () => {
    if (detail) {
      addEntity({
        type: "checklist-item",
        id: item.id,
        label: item.description.slice(0, 60),
      });
    }
  };

  const docCount =
    detail?.linkedDocuments.length ??
    ("linkedDocCount" in item ? item.linkedDocCount : 0);
  const clauseCount =
    detail?.clauseRefs.length ??
    ("clauseRefs" in item ? item.clauseRefs.length : 0);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex items-start gap-3 w-full p-3 rounded-lg border border-border/60 bg-surface hover:bg-card-hover/50 transition-colors text-left group"
    >
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
        style={{
          backgroundColor: statusConfig
            ? statusConfig.badgeBg
            : "rgba(108,108,112,0.15)",
        }}
      >
        <CheckSquare
          size={16}
          style={{ color: statusConfig?.badgeText ?? "#A1A1A6" }}
        />
      </div>
      <div className="flex-1 min-w-0">
        {/* Description */}
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-primary line-clamp-2">
            {item.description}
          </span>
          {detail && (
            <ExternalLink
              size={12}
              className="text-muted opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            />
          )}
        </div>

        {/* Metadata row */}
        <div className="flex items-center gap-2 mt-1">
          {statusConfig && (
            <Badge
              color={{ bg: statusConfig.badgeBg, text: statusConfig.badgeText }}
              className="px-2 py-0.5 text-[10px]"
            >
              {statusConfig.label}
            </Badge>
          )}
          {item.required && (
            <Badge
              color={{ bg: "rgba(239,68,68,0.1)", text: "#ef4444" }}
              className="px-2 py-0.5 text-[10px]"
            >
              {t("checklistCard.required")}
            </Badge>
          )}
          {docCount > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-muted">
              <FileText size={10} />
              {docCount}
            </span>
          )}
          {clauseCount > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-muted">
              <Scale size={10} />
              {clauseCount}
            </span>
          )}
        </div>

        {/* Detail variant extras */}
        {variant === "detail" && detail && (
          <div className="mt-2 space-y-1.5">
            {/* Category */}
            <span className="text-[10px] text-muted uppercase tracking-wider">
              {detail.categoryName}
            </span>

            {/* Summary excerpt */}
            {detail.summaryReport && (
              <p className="text-[11px] text-muted italic line-clamp-2 border-l-2 border-accent/30 pl-2">
                {detail.summaryReport.slice(0, 200)}
              </p>
            )}

            {/* Linked documents */}
            {detail.linkedDocuments.length > 0 && (
              <div className="space-y-0.5">
                <span className="text-[10px] text-muted font-medium">
                  {t("checklistCard.linkedDocuments")}
                </span>
                {detail.linkedDocuments.slice(0, 3).map((doc) => (
                  <div
                    key={doc.documentId}
                    className="flex items-center gap-1 text-[11px] text-muted"
                  >
                    <FileText size={10} className="shrink-0" />
                    <span className="truncate">{doc.fileName}</span>
                  </div>
                ))}
                {detail.linkedDocuments.length > 3 && (
                  <span className="text-[10px] text-accent">
                    {t("checklistCard.nMore", {
                      count: detail.linkedDocuments.length - 3,
                    })}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </button>
  );
}
